import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  Plus,
  Search,
  Phone,
  Globe,
  Mail,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import BillingSidebar from "@/components/layout/BillingSidebar";
import BillingHeader from "@/components/layout/BillingHeader";
import { supabase } from "@/integrations/supabase/client";

interface PayerInfo {
  id: string;
  name: string;
  type: string;
  claimsCount: number;
  approvedCount: number;
  deniedCount: number;
  pendingCount: number;
  totalBilled: number;
  totalPaid: number;
  avgDaysToPayment: number;
}

// Static payer information (in production, this would come from a database)
const payerDetails: Record<string, { phone: string; website: string; email: string; portalUrl: string }> = {
  medicare: {
    phone: "1-800-633-4227",
    website: "www.medicare.gov",
    email: "claims@medicare.gov",
    portalUrl: "https://portal.medicare.gov",
  },
  medicaid: {
    phone: "1-800-541-2831",
    website: "www.medicaid.gov",
    email: "claims@medicaid.gov",
    portalUrl: "https://portal.medicaid.gov",
  },
  dual_eligible: {
    phone: "1-800-633-4227",
    website: "www.medicare.gov/your-medicare-costs/get-help-paying-costs/medicare-savings-programs",
    email: "dualeligible@cms.gov",
    portalUrl: "https://portal.medicare.gov",
  },
};

export default function PayerManagement() {
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch claims to calculate payer metrics
  const { data: payers, isLoading } = useQuery({
    queryKey: ["payer-metrics"],
    queryFn: async () => {
      const { data: sessions, error } = await supabase
        .from("telehealth_sessions")
        .select("*");

      if (error) throw error;
      if (!sessions) return [];

      const memberIds = [...new Set(sessions.map(s => s.member_id))];
      const { data: intakeForms } = await supabase
        .from("patients")
        .select("user_id, insurance_type")
        .in("user_id", memberIds);

      // Calculate metrics by payer
      const payerMap = new Map<string, PayerInfo>();

      sessions.forEach(session => {
        const intake = intakeForms?.find(i => i.user_id === session.member_id);
        const payerType = intake?.insurance_type || "medicare";
        
        if (!payerMap.has(payerType)) {
          payerMap.set(payerType, {
            id: payerType,
            name: payerType === "dual_eligible" ? "Dual Eligible" : payerType.charAt(0).toUpperCase() + payerType.slice(1),
            type: payerType,
            claimsCount: 0,
            approvedCount: 0,
            deniedCount: 0,
            pendingCount: 0,
            totalBilled: 0,
            totalPaid: 0,
            avgDaysToPayment: 0,
          });
        }

        const payer = payerMap.get(payerType)!;
        payer.claimsCount++;
        
        // Estimate amount based on CPT code
        const amount = 125; // Default amount
        payer.totalBilled += amount;

        switch (session.billing_status) {
          case "approved":
            payer.approvedCount++;
            payer.totalPaid += amount;
            break;
          case "denied":
            payer.deniedCount++;
            break;
          case "pending":
          case "submitted":
            payer.pendingCount++;
            break;
        }
      });

      // Calculate average days to payment (mock data for now)
      payerMap.forEach(payer => {
        payer.avgDaysToPayment = payer.type === "medicare" ? 14 : payer.type === "medicaid" ? 21 : 18;
      });

      return Array.from(payerMap.values());
    },
  });

  const filteredPayers = payers?.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const getApprovalRate = (payer: PayerInfo) => {
    const total = payer.approvedCount + payer.deniedCount;
    if (total === 0) return 0;
    return Math.round((payer.approvedCount / total) * 100);
  };

  return (
    <div className="flex min-h-screen bg-background">
      <BillingSidebar />
      <div className="flex-1 flex flex-col">
        <BillingHeader />
        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Payer Management</h1>
                <p className="text-muted-foreground">Track payer performance and manage relationships</p>
              </div>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search payers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Payer Cards */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              </div>
            ) : (
              <div className="grid gap-6">
                {filteredPayers.map((payer) => {
                  const details = payerDetails[payer.type];
                  const approvalRate = getApprovalRate(payer);

                  return (
                    <Card key={payer.id}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Building2 className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                              <CardTitle>{payer.name}</CardTitle>
                              <CardDescription>Government Insurance Program</CardDescription>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-success border-success">
                            Active
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Contact Info */}
                        {details && (
                          <div className="grid grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{details.phone}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Globe className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{details.website}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{details.email}</span>
                            </div>
                            <Button variant="outline" size="sm" asChild>
                              <a href={details.portalUrl} target="_blank" rel="noopener noreferrer">
                                Open Portal
                              </a>
                            </Button>
                          </div>
                        )}

                        <Separator />

                        {/* Metrics */}
                        <div className="grid grid-cols-5 gap-4">
                          <div className="text-center p-4 bg-muted/30 rounded-lg">
                            <p className="text-2xl font-bold">{payer.claimsCount}</p>
                            <p className="text-sm text-muted-foreground">Total Claims</p>
                          </div>
                          <div className="text-center p-4 bg-success/10 rounded-lg">
                            <p className="text-2xl font-bold text-success">{payer.approvedCount}</p>
                            <p className="text-sm text-muted-foreground">Approved</p>
                          </div>
                          <div className="text-center p-4 bg-warning/10 rounded-lg">
                            <p className="text-2xl font-bold text-warning">{payer.pendingCount}</p>
                            <p className="text-sm text-muted-foreground">Pending</p>
                          </div>
                          <div className="text-center p-4 bg-destructive/10 rounded-lg">
                            <p className="text-2xl font-bold text-destructive">{payer.deniedCount}</p>
                            <p className="text-sm text-muted-foreground">Denied</p>
                          </div>
                          <div className="text-center p-4 bg-accent/10 rounded-lg">
                            <p className="text-2xl font-bold text-accent">{payer.avgDaysToPayment}</p>
                            <p className="text-sm text-muted-foreground">Avg Days to Pay</p>
                          </div>
                        </div>

                        {/* Approval Rate */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Approval Rate</span>
                            <span className="text-sm font-bold">{approvalRate}%</span>
                          </div>
                          <Progress 
                            value={approvalRate} 
                            className="h-2"
                          />
                        </div>

                        {/* Financial Summary */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 border rounded-lg">
                            <div className="flex items-center gap-2 text-muted-foreground mb-1">
                              <FileText className="h-4 w-4" />
                              <span className="text-sm">Total Billed</span>
                            </div>
                            <p className="text-2xl font-bold">${payer.totalBilled.toLocaleString()}</p>
                          </div>
                          <div className="p-4 border rounded-lg">
                            <div className="flex items-center gap-2 text-muted-foreground mb-1">
                              <CheckCircle2 className="h-4 w-4" />
                              <span className="text-sm">Total Collected</span>
                            </div>
                            <p className="text-2xl font-bold text-success">${payer.totalPaid.toLocaleString()}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                {filteredPayers.length === 0 && !isLoading && (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium">No payers found</h3>
                      <p className="text-muted-foreground">Submit claims to see payer analytics</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
